import React, { useCallback, useEffect, useRef, useState } from "react";
import type { OnboardingStepDTO } from "front-lib";
import { MdClose, MdArrowForward, MdArrowBack } from "react-icons/md";

type OnboardingOverlayProps = {
    isActive: boolean;
    step: OnboardingStepDTO | null;
    currentIndex: number;
    totalSteps: number;
    onNext: () => void;
    onPrevious: () => void;
    onSkip: () => void;
};

type TargetRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const PADDING = 8;
const TOOLTIP_WIDTH = 340;

function getTooltipPosition(
    rect: TargetRect | null,
    position: OnboardingStepDTO["position"] = "bottom",
): React.CSSProperties {
    if (!rect) {
        // centered floating tooltip
        return {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
        };
    }

    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    switch (position) {
        case "top":
            return {
                top: rect.top + scrollY - PADDING - 12,
                left: Math.max(
                    16,
                    Math.min(
                        rect.left + scrollX + rect.width / 2 - TOOLTIP_WIDTH / 2,
                        window.innerWidth - TOOLTIP_WIDTH - 16,
                    ),
                ),
                transform: "translateY(-100%)",
            };
        case "left":
            return {
                top: rect.top + scrollY + rect.height / 2,
                left: rect.left + scrollX - TOOLTIP_WIDTH - PADDING - 12,
                transform: "translateY(-50%)",
            };
        case "right":
            return {
                top: rect.top + scrollY + rect.height / 2,
                left: rect.left + scrollX + rect.width + PADDING + 12,
                transform: "translateY(-50%)",
            };
        case "center":
            return {
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
            };
        case "bottom":
        default:
            return {
                top: rect.top + scrollY + rect.height + PADDING + 12,
                left: Math.max(
                    16,
                    Math.min(
                        rect.left + scrollX + rect.width / 2 - TOOLTIP_WIDTH / 2,
                        window.innerWidth - TOOLTIP_WIDTH - 16,
                    ),
                ),
            };
    }
}

export default function OnboardingOverlay({
    isActive,
    step,
    currentIndex,
    totalSteps,
    onNext,
    onPrevious,
    onSkip,
}: OnboardingOverlayProps) {
    const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
    const [visible, setVisible] = useState(false);
    const animRef = useRef<number>(0);

    const measureTarget = useCallback(() => {
        if (!step?.targetId) {
            setTargetRect(null);
            return;
        }
        const el = document.getElementById(step.targetId);
        if (!el) {
            setTargetRect(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        setTargetRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });

        // Scroll element into view if needed
        const viewportH = window.innerHeight;
        if (rect.top < 80 || rect.bottom > viewportH - 80) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Re-measure after scroll
            setTimeout(() => {
                if (!step?.targetId) return;
                const currentEl = document.getElementById(step.targetId);
                if (!currentEl || currentEl !== el) return;
                const r2 = el.getBoundingClientRect();
                setTargetRect({
                    top: r2.top,
                    left: r2.left,
                    width: r2.width,
                    height: r2.height,
                });
            }, 400);
        }
    }, [step?.targetId]);

    useEffect(() => {
        if (isActive && step) {
            measureTarget();
            // Animate in
            animRef.current = window.setTimeout(() => setVisible(true), 50);
            const handleResize = () => measureTarget();
            window.addEventListener("resize", handleResize);
            window.addEventListener("scroll", handleResize, true);
            return () => {
                clearTimeout(animRef.current);
                window.removeEventListener("resize", handleResize);
                window.removeEventListener("scroll", handleResize, true);
            };
        } else {
            setVisible(false);
            setTargetRect(null);
        }
    }, [isActive, step, measureTarget]);

    if (!isActive || !step) return null;

    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    // Spotlight hole (CSS clip-path)
    const clipPath = targetRect
        ? `polygon(
        0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
        ${targetRect.left - PADDING + scrollX}px ${targetRect.top - PADDING + scrollY}px,
        ${targetRect.left - PADDING + scrollX}px ${targetRect.top + targetRect.height + PADDING + scrollY}px,
        ${targetRect.left + targetRect.width + PADDING + scrollX}px ${targetRect.top + targetRect.height + PADDING + scrollY}px,
        ${targetRect.left + targetRect.width + PADDING + scrollX}px ${targetRect.top - PADDING + scrollY}px,
        ${targetRect.left - PADDING + scrollX}px ${targetRect.top - PADDING + scrollY}px
      )`
        : undefined;

    const tooltipStyle = getTooltipPosition(targetRect, step.position);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === totalSteps - 1;

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 99999,
                pointerEvents: isActive ? "auto" : "none",
                opacity: visible ? 1 : 0,
                transition: "opacity 0.3s ease",
            }}
        >
            {/* Backdrop */}
            <div
                onClick={onSkip}
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0, 0, 0, 0.65)",
                    clipPath,
                    transition: "clip-path 0.4s ease",
                }}
            />

            {/* Target highlight ring */}
            {targetRect && (
                <div
                    style={{
                        position: "absolute",
                        top: targetRect.top - PADDING,
                        left: targetRect.left - PADDING,
                        width: targetRect.width + PADDING * 2,
                        height: targetRect.height + PADDING * 2,
                        borderRadius: 12,
                        border: "2px solid rgba(6, 182, 212, 0.6)",
                        boxShadow: "0 0 0 4px rgba(6, 182, 212, 0.15), 0 0 24px rgba(6, 182, 212, 0.15)",
                        pointerEvents: "none",
                        transition: "all 0.4s ease",
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                style={{
                    position: "absolute",
                    width: TOOLTIP_WIDTH,
                    ...tooltipStyle,
                    zIndex: 100000,
                    transition: "all 0.35s ease",
                }}
            >
                <div
                    style={{
                        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                        border: "1px solid rgba(6, 182, 212, 0.3)",
                        borderRadius: 16,
                        padding: "20px 22px 16px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.45), 0 0 1px rgba(6,182,212,0.3)",
                        color: "#fff",
                        fontFamily: "Inter, system-ui, sans-serif",
                    }}
                >
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "rgba(6, 182, 212, 0.8)",
                                letterSpacing: 1,
                                textTransform: "uppercase",
                            }}
                        >
                            {currentIndex + 1} / {totalSteps}
                        </span>
                        <button
                            onClick={onSkip}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "rgba(255,255,255,0.4)",
                                padding: 2,
                                display: "flex",
                                transition: "color 0.2s",
                            }}
                            aria-label="Fechar"
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#fff"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.4)"; }}
                        >
                            <MdClose size={18} />
                        </button>
                    </div>

                    {/* Title */}
                    <h3
                        style={{
                            fontSize: 16,
                            fontWeight: 700,
                            margin: "0 0 6px",
                            lineHeight: 1.3,
                            color: "#fff",
                        }}
                    >
                        {step.title}
                    </h3>

                    {/* Description */}
                    <p
                        style={{
                            fontSize: 13,
                            lineHeight: 1.55,
                            margin: "0 0 16px",
                            color: "rgba(255,255,255,0.7)",
                        }}
                    >
                        {step.description}
                    </p>

                    {/* Progress bar */}
                    <div
                        style={{
                            width: "100%",
                            height: 3,
                            borderRadius: 3,
                            background: "rgba(255,255,255,0.08)",
                            marginBottom: 14,
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                height: "100%",
                                width: `${((currentIndex + 1) / totalSteps) * 100}%`,
                                borderRadius: 3,
                                background: "linear-gradient(90deg, #06b6d4, #8b5cf6)",
                                transition: "width 0.4s ease",
                            }}
                        />
                    </div>

                    {/* Buttons */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <button
                            onClick={onSkip}
                            style={{
                                background: "none",
                                border: "none",
                                color: "rgba(255,255,255,0.45)",
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: "pointer",
                                padding: "4px 0",
                                transition: "color 0.2s",
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#fff"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.45)"; }}
                        >
                            Pular
                        </button>

                        <div style={{ display: "flex", gap: 8 }}>
                            {!isFirst && (
                                <button
                                    onClick={onPrevious}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                        padding: "7px 14px",
                                        borderRadius: 10,
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        background: "rgba(255,255,255,0.05)",
                                        color: "#fff",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.1)"; }}
                                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; }}
                                >
                                    <MdArrowBack size={14} />
                                    Voltar
                                </button>
                            )}
                            <button
                                onClick={onNext}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "7px 18px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "linear-gradient(135deg, #06b6d4, #8b5cf6)",
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    boxShadow: "0 2px 12px rgba(6, 182, 212, 0.3)",
                                }}
                                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1.04)"; }}
                                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
                            >
                                {isLast ? "Concluir" : "Próximo"}
                                {!isLast && <MdArrowForward size={14} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
